import { z } from 'zod';
import { type OrgOpSignableDecision } from './decision';
import type { DomainInvalidReason, ProposalValidity } from './proposalValidity';

const disasterRecoveryProposalActionSchema = z.enum([
  'INITIATE_INITIALIZE_TX_RECOVERY',
  'CANCEL_INITIALIZE_TX_RECOVERY',
  'FINALIZE_INITIALIZE_TX_RECOVERY',
]);
export const DisasterRecoveryProposalAction =
  disasterRecoveryProposalActionSchema.enum;
export type DisasterRecoveryProposalActionValue = z.infer<
  typeof disasterRecoveryProposalActionSchema
>;

export type DisasterRecoveryProposalStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'REJECTED';

/**
 * Status of an on-chain DR record linked to proposals.
 * It reuses the same action vocabulary as DR proposal actions.
 */
export type DisasterRecoveryRecordStatus = DisasterRecoveryProposalActionValue;

/**
 * Lifecycle phase of an active DR configuration.
 */
export type DisasterRecoveryPhase =
  | 'TIMELOCK_WAIT'
  | 'FINALIZATION_READY'
  | 'COMPLETED';

/** Default org admin-operation timelock used at deploy (3 days). */
export const DEFAULT_ADMIN_OPERATION_TIMELOCK_SECONDS = 60 * 60 * 24 * 3;

const SECONDS_PER_DAY = 60 * 60 * 24;
const MIN_RECOVERY_TIMELOCK_DAYS = 2;
const MAX_RECOVERY_TIMELOCK_DAYS = 30;
export const MIN_RECOVERY_TIMELOCK_SECONDS =
  MIN_RECOVERY_TIMELOCK_DAYS * SECONDS_PER_DAY;
export const MAX_RECOVERY_TIMELOCK_SECONDS =
  MAX_RECOVERY_TIMELOCK_DAYS * SECONDS_PER_DAY;

/**
 * DR configuration payload returned to clients for settings rendering.
 */
export type DisasterRecoveryConfig = {
  id: string;
  recoveryWalletAddress: string;
  /** Recovery-wallet usage timelock configured during setup, in seconds. */
  timelockDurationSeconds: number;
  /** Org admin-operation timelock governing setup initiate → finalize wait. */
  adminOperationTimelockDays: number;
  phase: DisasterRecoveryPhase;
  /** When admins can finalize setup (admin-operation timelock expiry). */
  setupTimelockExpiresAt: string | null;
};

/**
 * UI-facing proposal shape for INITIATE actions.
 */
export type DisasterRecoveryInitiationProposal = {
  id: string;
  status: DisasterRecoveryProposalStatus;
  validity: ProposalValidity;
  domainInvalidReason?: DomainInvalidReason;
  organizationId: string;
  createdByUserId: string;
  createdByUserName?: string;
  recoveryWalletAddress: string;
  /** Recovery-wallet usage timelock from the proposal, in seconds. */
  timelockDurationSeconds: number;
  createdAt: string;
  updatedAt: string;
};

/**
 * UI-facing proposal shape for CANCEL/FINALIZE actions.
 */
export type DisasterRecoverySupportingProposal = {
  id: string;
  status: DisasterRecoveryProposalStatus;
  validity: ProposalValidity;
  domainInvalidReason?: DomainInvalidReason;
  createdByUserId: string;
  createdByUserName?: string;
  createdAt: string;
};

/**
 * Signature fields needed by DR proposal review UI.
 * Database-backed signature rows may include additional fields; those remain
 * structurally compatible with this shared subset.
 */
export type DisasterRecoveryReviewSignature = {
  reviewingUserId: string;
  decision: OrgOpSignableDecision;
  signature: string | null;
};

/**
 * Shared review-data wrapper for any DR proposal shape.
 */
export type DRProposalWithReviewData<TProposal> = {
  proposal: TProposal;
  isExecuting: boolean;
  approvals: DisasterRecoveryReviewSignature[];
  rejections: DisasterRecoveryReviewSignature[];
  linkedDisasterRecoveryStatus: DisasterRecoveryRecordStatus | null;
  requiredApprovals: number;
  totalVoters: number;
};

/**
 * Canonical DR settings payload returned by sync server and consumed by UI.
 */
export type DisasterRecoverySettingsResponse = {
  drConfig: DisasterRecoveryConfig | null;
  drInitiationProposal: DRProposalWithReviewData<DisasterRecoveryInitiationProposal> | null;
  drCancellationProposal: DRProposalWithReviewData<DisasterRecoverySupportingProposal> | null;
  drFinalizationProposal: DRProposalWithReviewData<DisasterRecoverySupportingProposal> | null;
};

const createInitiateDisasterRecoveryProposalInputSchema = z.object({
  action: z.literal(
    DisasterRecoveryProposalAction.INITIATE_INITIALIZE_TX_RECOVERY
  ),
  recoveryAddress: z.string(),
  timelockDurationSeconds: z
    .number()
    .int()
    .min(MIN_RECOVERY_TIMELOCK_SECONDS)
    .max(MAX_RECOVERY_TIMELOCK_SECONDS),
});

const createCancelOrFinalizeDisasterRecoveryProposalInputSchema = z.object({
  action: z.union([
    z.literal(DisasterRecoveryProposalAction.CANCEL_INITIALIZE_TX_RECOVERY),
    z.literal(DisasterRecoveryProposalAction.FINALIZE_INITIALIZE_TX_RECOVERY),
  ]),
  disasterRecoveryId: z.string().uuid(),
});

export const createDisasterRecoveryProposalInputSchema = z.union([
  createInitiateDisasterRecoveryProposalInputSchema,
  createCancelOrFinalizeDisasterRecoveryProposalInputSchema,
]);
export type CreateDisasterRecoveryProposalInput = z.infer<
  typeof createDisasterRecoveryProposalInputSchema
>;
